import orderBy from "lodash.orderby";
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useImmer } from "use-immer";

import {
  IgAvatar,
  IgChip,
  IgTooltip,
  IgTypography,
} from "@intelligems/ig-theme";
import { OrganizationListDto } from "@intelligems/ig-types";
import { Box, Grid, Skeleton, useTheme } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useGetOrg } from "@src/api/organizations/get-organization";
import { useGetOrgList } from "@src/api/organizations-list/get-organization-list";
import { OrgList } from "@src/components/Navigation/OrgPicker/OrgList";
import { IgWhiteGemIcon } from "@src/images/Icons/Intelligems/IgWhiteGem";
import { GENERAL_ROUTES } from "@src/routes/routes";
import { useDispatch } from "@src/store";
import { setSideNavCollapsed } from "@src/store/slices/appSettings";
import { SideNavCollapseControl } from "@src/utils/SideNavCollapseControl";

interface OrgPickerProps {
  redirectToHome?: boolean;
  hidePicker?: boolean;
  drawerOpen: boolean;
}

const PREFIX = "OrgPicker";

const classes = {
  orgName: `${PREFIX}-orgName`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`& .${classes.orgName}`]: {
    fontWeight: 500,
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    overflow: "hidden",
    width: "234px",
  },

  display: "flex",
  flexDirection: "column",
  // padding: "16px 8px",
}));

export const OrgPicker = ({
  redirectToHome,
  hidePicker,
  drawerOpen,
}: OrgPickerProps) => {
  const [nameBy, setNameBy] = useImmer<"name" | "shopId">("name");
  const [order, setOrder] = useImmer<"name" | "date">("name");
  const theme = useTheme();
  const organization = useGetOrg();
  const navigate = useNavigate();
  const orgList = useGetOrgList();
  const dispatch = useDispatch();
  const options: OrganizationListDto[] = useMemo(
    () =>
      orderBy(
        orgList.data?.orgs || [],
        order === "name" ? ["name"] : ["createdAtTs"],
        order === "name" ? ["asc"] : ["desc"]
      ).map((org: OrganizationListDto) => ({
        id: org.id,
        name:
          nameBy === "name"
            ? org.name
            : org.shopId.replace(".myshopify.com", ""),
        shopId: org.shopId,
        isInstalled: org.isInstalled,
        createdAtTs: org.createdAtTs,
        roles: org.roles,
      })),
    [orgList.data?.orgs, nameBy, order]
  );

  return (
    <StyledBox
      data-clarity-unmask={true}
      onClick={() => {
        if (redirectToHome) {
          void navigate(GENERAL_ROUTES.Index);
        } else {
          if (window.location.pathname !== GENERAL_ROUTES.Index) {
            void navigate(GENERAL_ROUTES.Index);
          }
        }
      }}
      sx={{
        // height: hidePicker ? "120px" : undefined,
        cursor: redirectToHome
          ? "pointer"
          : window.location.pathname === GENERAL_ROUTES.Index
            ? "auto"
            : "pointer",
        zIndex: 1360,
        py: 4,
        px: drawerOpen ? 2 : 4,
        // px: drawerOpen ?
      }}
    >
      <Grid container direction={"column"} spacing={4}>
        <Grid
          item
          direction={"row"}
          container
          justifyContent={"space-between"}
          wrap={"nowrap"}
          alignItems={"center"}
        >
          {import.meta.env.VITE_UI_URL === "https://beta.intelligems.io" ? (
            <IgChip
              color={"accent"}
              label={"Beta"}
              size={"small"}
              sx={{
                position: "absolute",
                top: 45,
                right: 19,
                minWidth: "45px",
                fontSize: "10px",
                zIndex: 999999,
                padding: "2px",
              }}
            />
          ) : null}
          <Grid
            item
            container
            direction={"row"}
            spacing={2}
            justifyContent={"flex-start"}
            alignItems={"center"}
          >
            <Grid item>
              <IgTooltip
                placement={"right"}
                title={drawerOpen ? "" : "Expand Navigation"}
              >
                <IgAvatar
                  onClick={() => {
                    dispatch(setSideNavCollapsed());
                  }}
                  variant="rounded"
                  sx={{
                    backgroundColor: theme.igPrimitives.base[500],
                    "&:hover": { cursor: "pointer" },
                  }}
                >
                  <IgWhiteGemIcon color={"dark"} />
                </IgAvatar>
              </IgTooltip>
            </Grid>

            {drawerOpen && (
              <Grid item>
                <IgTypography variant={"h2"} fontWeight={600}>
                  intelligems
                </IgTypography>
              </Grid>
            )}
          </Grid>
          {drawerOpen && <SideNavCollapseControl />}
        </Grid>
        {hidePicker || !drawerOpen ? null : (
          <Grid container item>
            {orgList.isSuccess && orgList.data.orgs ? (
              orgList.data.orgs.length > 1 && !redirectToHome ? (
                <Grid
                  container
                  item
                  direction={"row"}
                  flexWrap={"nowrap"}
                  columnSpacing={1}
                >
                  {/*<OrgListFilter*/}
                  {/*  nameBy={nameBy}*/}
                  {/*  setNameBy={setNameBy}*/}
                  {/*  order={order}*/}
                  {/*  setOrder={setOrder}*/}
                  {/*/>*/}
                  <OrgList options={options} />
                </Grid>
              ) : (
                <Grid container item justifyContent={"center"}>
                  <Grid item>
                    <IgTypography className={classes.orgName}>
                      {organization.data.org.name}
                    </IgTypography>
                  </Grid>
                </Grid>
              )
            ) : (
              <Grid item sx={{ marginTop: "-13px" }}>
                <Skeleton
                  sx={{
                    height: "39px",
                    width: "160px",
                    backgroundColor: "rgb(73 76 80);",
                  }}
                />
              </Grid>
            )}
          </Grid>
        )}
      </Grid>
    </StyledBox>
  );
};
